import clsx from 'clsx'
import Link from 'next/link'
import type { ReactNode, SyntheticEvent } from 'react'
import React from 'react'
import style from './Button.module.sass'

type ButtonButtonProps = {
	buttonType?: 'button' | 'submit'
	ariaLabel?: string
}

type ButtonAnchorProps = {
	url: string | { pathname: string; query: { [key: string]: string | string[] | undefined } }
	isTargetBlank?: boolean
}

export type ButtonProps = {
	type?: {
		variant?:
			| 'bordered'
			| 'naked'
			| 'nakedMore'
			| 'nakedIcon'
			| 'primary'
			| 'underlined'
			| 'white'
			| 'transparent'
			| 'underlinedGrey'
			| 'extraLarge'
		large?: boolean
		small?: boolean
		fullWidth?: boolean
		greyColor?: boolean
		borderRadiusSmall?: boolean
	}
	iconButtonStyle?: {
		color?: 'white' | 'primary' | 'grey' | 'whiteBordered'
		size?: 'extraSmall' | 'small' | 'mediumSmall'
	}
	icon?: {
		rotatedHorizontally?: boolean
		fillPrimary?: boolean
		chevronDown?: boolean
		fillGrey?: boolean
		large?: boolean
		small?: boolean
		extraSmall?: boolean
		strokeWhite?: boolean
		strokePrimary?: boolean
		aloneStanding?: boolean
	}
	disabled?: boolean
	label: ReactNode
	dataTrixam?: string
	noShrink?: boolean
	hoverBackground?: boolean
	iconBefore?: JSX.Element | undefined
	iconAfter?: JSX.Element | undefined
	onClick?: (event: SyntheticEvent) => void
} & (ButtonButtonProps | ButtonAnchorProps)

export const Button = React.memo(function Button(props: ButtonProps) {
	const basedIconClassName = clsx(
		props.icon?.fillPrimary && style.iconFillPrimary,
		props.icon?.chevronDown && style.chevronDown,
		props.icon?.large && style.iconLarge,
		props.icon?.small && style.iconSmall,
		props.icon?.extraSmall && style.iconExtraSmall,
		props.icon?.fillGrey && style.iconFillGrey,
		props.icon?.strokeWhite && style.iconStrokeWhite,
		props.icon?.strokePrimary && style.iconStrokePrimary,
		props.icon?.rotatedHorizontally && style.iconRotate,
	)

	const wrapperClassName = clsx(
		props.disabled && style.disabled,
		style.button,
		props.hoverBackground && style.buttonHoverBackground,
		props.noShrink && style.buttonNoShrink,
		style[`view_button_variant_${props.type?.variant ?? 'primary'}`],
		props.type?.borderRadiusSmall && style.buttonBorderRadiusSmall,
		props.type?.greyColor && style.buttonGreyColor,
		props.type?.large && style.buttonLarge,
		props.type?.small && style.buttonSmall,
		props.type?.fullWidth && style.buttonFullWidth,
		props.iconButtonStyle && style.iconButton,
		style[`view_buttonIcon_color_${props.iconButtonStyle?.color ?? ''}`],
		style[`view_icon_size_${props.iconButtonStyle?.size}`],
	)

	const labelClassName = clsx(
		props.icon?.fillPrimary && style.iconFillPrimary,
		props.icon?.aloneStanding && style.iconCenter,
		props.icon?.rotatedHorizontally && props.icon?.aloneStanding && style.iconRotate,
	)

	const content = (
		<>
			{props.iconBefore && <span className={clsx(style.iconBefore, basedIconClassName)}>{props.iconBefore}</span>}

			<span className={clsx(style.label, labelClassName)}>{props.label}</span>

			{props.iconAfter && <span className={clsx(style.iconAfter, basedIconClassName)}>{props.iconAfter}</span>}
		</>
	)

	if ('url' in props) {
		return (
			<Link
				href={props.url}
				onClick={props.onClick}
				className={wrapperClassName}
				data-trixam-positionid={props.dataTrixam ?? ''}
				target={props.isTargetBlank ? '_blank' : ''}
			>
				{content}
			</Link>
		)
	}

	return (
		<button
			type={props.buttonType ?? 'button'}
			onClick={props.onClick}
			disabled={props.disabled}
			aria-label={props.ariaLabel}
			className={wrapperClassName}
		>
			{content}
		</button>
	)
})
